import { createContext, useContext, useMemo, createElement, useState, useRef, useEffect } from 'react';

class Container {
    constructor() {
        this.listeners = new Set();
    }
    setState(updater) {
        const nextState = typeof updater === 'function' ? updater(this.state) : updater;
        if (nextState == null) {
            return;
        }
        this.state = Object.assign({}, this.state, nextState);
        this.listeners.forEach(listener => {
            listener();
        });
    }
    subscribe(fn) {
        this.listeners.add(fn);
    }
    unsubscribe(fn) {
        this.listeners.delete(fn);
    }
}

const StateContext = createContext(null);
function Provider(props) {
    const { inject, children } = props;
    const parentMap = useContext(StateContext);
    const childMap = useMemo(() => {
        const newChildMap = new Map(parentMap);
        if (inject) {
            inject.forEach(instance => {
                newChildMap.set(instance.constructor, instance);
            });
        }
        return newChildMap;
    }, [parentMap, ...(inject || [])]);
    return (createElement(StateContext.Provider, { value: childMap }, children));
}

const hasOwn = Object.prototype.hasOwnProperty;
function is(x, y) {
    if (x === y) {
        return x !== 0 || y !== 0 || 1 / x === 1 / y;
    }
    return false;
}
function shallowEqual(objA, objB) {
    if (is(objA, objB)) {
        return true;
    }
    if (typeof objA !== 'object' ||
        objA === null ||
        typeof objB !== 'object' ||
        objB === null) {
        return false;
    }
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) {
        return false;
    }
    for (const keyA in keysA) {
        if (!hasOwn.call(objB, keyA) || !is(objA[keyA], objB[keyA])) {
            return false;
        }
    }
    return true;
}

function useContainer(ContainerItem) {
    const map = useContext(StateContext);
    if (map === null) {
        throw new Error('You must wrap your hook component with a <Provider>');
    }
    return useMemo(() => {
        let instance;
        if (typeof ContainerItem === 'object' &&
            ContainerItem instanceof Container) {
            instance = ContainerItem;
        }
        else {
            instance = map.get(ContainerItem);
            if (!instance) {
                instance = new ContainerItem();
                map.set(ContainerItem, instance);
            }
        }
        return instance;
    }, [map, ContainerItem]);
}
function useUnstated(ContainerItem, shouldUpdate) {
    const instance = useContainer(ContainerItem);
    const setUpdates = useState(0)[1];
    const latestStateRef = useRef(null);
    const updateCriteriaRef = useRef(null);
    const unmountedRef = useRef(false);
    const shouldUpdateRef = useRef(shouldUpdate);
    shouldUpdateRef.current = shouldUpdate;
    useEffect(() => {
        const computeShouldRender = () => {
            if (!shouldUpdateRef.current) {
                if (latestStateRef.current !== instance.state) {
                    latestStateRef.current = instance.state;
                    return true;
                }
                return false;
            }
            const result = shouldUpdateRef.current(instance);
            const isEqual = !shallowEqual(updateCriteriaRef.current, result);
            updateCriteriaRef.current = result;
            return isEqual;
        };
        const onUpdate = () => {
            if (!computeShouldRender()) {
                return;
            }
            if (!unmountedRef.current) {
                setUpdates(prev => prev + 1);
            }
        };
        instance.subscribe(onUpdate);
        onUpdate();
        return () => {
            unmountedRef.current = true;
            instance.unsubscribe(onUpdate);
        };
    }, [instance]);
    latestStateRef.current = instance.state;
    return instance;
}

function Subscribe(props) {
    const { to, children } = props;
    const instance = useUnstated(Array.isArray(to) ? to[0] : to);
    return children(instance);
}

export { Container, Provider, Subscribe, useContainer, useUnstated };
